/**
 * Chain-restaurant registry.
 *
 * A restaurant row in our DB that represents a chain (Chili's,
 * Chipotle, Shake Shack) typically has a single Google Place's
 * address attached — e.g. the one location the crawl happened to
 * land on. Showing that specific location's address + directions
 * to a user elsewhere in the country is actively misleading ("why
 * are you sending me to Canon City, CO?").
 *
 * We solve that by marking chain rows and rendering them differently:
 *
 *   - Location line: "Multiple locations · near you" instead of the
 *     specific street address.
 *   - Directions URL: "{name} near me" so Google Maps auto-resolves
 *     to the user's device location.
 *
 * This list is intentionally hand-maintained and covers the majority
 * of the national-chain venues seeded from our Facebook/Reddit
 * community crawls. Local chains and one-off restaurants should NOT
 * be in here — a diner or independent with one address is correctly
 * shown with its specific location.
 *
 * Add new chains here as they get imported. Normalization is via
 * stripLower() so "Chick-fil-A", "Chick Fil A" and "CHICK FIL A"
 * all collapse to the same key.
 */

const CHAIN_NAMES_RAW = [
  // Fast casual / fast food
  "Chipotle",
  "Cava",
  "Sweetgreen",
  "Shake Shack",
  "Five Guys",
  "Wendy's",
  "McDonald's",
  "Burger King",
  "Chick-fil-A",
  "Chili's",
  "Subway",
  "Jersey Mike's",
  "Jersey Mikes",
  "Firehouse Subs",
  "Panera Bread",
  "Panera",
  "Taco Bell",
  "KFC",
  "Popeyes",
  "Panda Express",
  "In-N-Out",
  "In-N-Out Burger",
  "Culver's",
  "Sonic",
  "Sonic Drive-In",
  "Arby's",
  "Hardee's",
  "Carl's Jr.",
  "Dairy Queen",
  "A&W",
  "Jack in the Box",
  "White Castle",
  "Checkers",
  "Rally's",
  "El Pollo Loco",
  "Qdoba",
  "Moe's Southwest Grill",
  "Moe's",
  "Wingstop",
  "Buffalo Wild Wings",

  // Sit-down chains
  "Olive Garden",
  "Red Lobster",
  "Applebee's",
  "TGI Friday's",
  "Denny's",
  "IHOP",
  "Cracker Barrel",
  "Outback Steakhouse",
  "Outback",
  "Texas Roadhouse",
  "LongHorn Steakhouse",
  "Capital Grille",
  "The Capital Grille",
  "Bonefish Grill",
  "Ruby Tuesday",
  "Cheesecake Factory",
  "The Cheesecake Factory",
  "Cheddar's Scratch Kitchen",
  "Cheddar's",
  "Bahama Breeze",
  "Bartaco",
  "Bareburger",
  "Bareburger NYC",
  "Beatrix",
  "Bibibop",
  "Mellow Mushroom",
  "110 Grill",
  "True Food Kitchen",
  "First Watch",
  "Silver Diner",

  // Pizza
  "Blaze Pizza",
  "MOD Pizza",
  "Domino's",
  "Domino's Pizza",
  "Pizza Hut",
  "Papa John's",
  "Papa Murphy's",
  "Little Caesars",
  "Marco's Pizza",
  "California Pizza Kitchen",

  // Bakery / breakfast / treats
  "Starbucks",
  "Dunkin'",
  "Dunkin Donuts",
  "Krispy Kreme",
  "Einstein Bros. Bagels",
  "Einstein Bros Bagels",
  "Bagel Boss",
  "Bagel Cafe",
  "Crumbl",
  "Crumbl Cookies",
  "Carvel",
  "Baskin-Robbins",
  "Cold Stone Creamery",
  "Cold Stone",
  "Dairy Queen Grill & Chill",
  "Jamba Juice",
  "Jamba",
  "Smoothie King",

  // Other
  "IKEA",
  "Boxyard Food Hall",
  "Pizza Express",
];

const stripLower = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\u2019']/g, "")   // curly + straight apostrophe
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const CHAIN_KEYS = new Set(CHAIN_NAMES_RAW.map(stripLower));

/**
 * Is this restaurant row a chain we should hide the specific address
 * for + route Directions to "near me"?
 *
 * Matches on the normalized restaurant.name against CHAIN_KEYS.
 */
export function isChain(restaurant) {
  if (!restaurant || !restaurant.name) return false;
  return CHAIN_KEYS.has(stripLower(restaurant.name));
}

/**
 * URL for the Directions button. For chains, bypass the specific
 * seeded address and let Google Maps resolve to the nearest branch
 * via the user's device location. For everything else, use the row's
 * canonical name + address (same behavior as before).
 */
export function directionsUrlFor(restaurant) {
  const name = (restaurant.name || "").trim();
  if (!name) return null;
  if (isChain(restaurant)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name + " near me")}`;
  }
  const address = (restaurant.address || "").trim();
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name + " " + address)}`;
}
